import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Clock, Heart, ExternalLink, Lock, Unlock, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAccount } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
// import { useInView } from 'react-intersection-observer'; // Removed for pagination
import toast from 'react-hot-toast';
import CoinModal from './CoinModal';
import { getApiBaseUrl } from '../utils/api';
import { debugLog } from '../utils/debug';
import { useOpenUrl } from '@coinbase/onchainkit/minikit';
import './TestimonyGallery.css';

const ConfessionGallery = forwardRef(({ onUnlockedCountChange }, ref) => {
  const [confessions, setConfessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 15;
  const [playingId, setPlayingId] = useState(null);
  const [mediaElements, setMediaElements] = useState({});
  const [selectedConfessionId, setSelectedConfessionId] = useState(null);
  const [isCoinModalOpen, setIsCoinModalOpen] = useState(false);
  const [tokenOwnership, setTokenOwnership] = useState({});
  const [checkingTokens, setCheckingTokens] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  const { address, isConnected } = useAccount();
  
  // Use MiniKit hook for external URL handling
  const openUrl = useOpenUrl();
  
  // Detect MiniApp mode
  const isMiniApp = window?.parent !== window || 
                   navigator.userAgent.includes('MiniApp') ||
                   window.location.search.includes('miniapp=true');
  
  // Removed infinite scroll logic

  useImperativeHandle(ref, () => ({
    switchToUnlockedTab: () => {
      setActiveTab('unlocked');
    }
  }));

  useEffect(() => {
    fetchConfessions();
  }, [currentPage]);

  // Removed infinite scroll effect

  useEffect(() => {
    if (isConnected && address && confessions.length > 0) {
      checkTokenOwnership();
    } else {
      setTokenOwnership({});
    }
  }, [isConnected, address, confessions]);

  // Notify parent component about unlocked count changes
  useEffect(() => {
    if (onUnlockedCountChange) {
      const unlockedCount = getUnlockedConfessions().length;
      onUnlockedCountChange(unlockedCount);
    }
  }, [tokenOwnership, confessions, onUnlockedCountChange]);

  const fetchConfessions = useCallback(async () => {
    try {
      setLoading(true);
      
      const apiBase = getApiBaseUrl();
      const response = await fetch(`${apiBase}/testimonies?page=${currentPage - 1}&limit=${ITEMS_PER_PAGE}`);
      const data = await response.json();
      
      if (data.testimonies && Array.isArray(data.testimonies)) {
        setConfessions(data.testimonies);
        setTotalCount(data.pagination?.total || 0);
        setTotalPages(Math.ceil((data.pagination?.total || 0) / ITEMS_PER_PAGE));
        
        debugLog(`📄 Loaded ${data.testimonies.length} confessions, page: ${currentPage}, total: ${data.pagination?.total}`);
      } else {
        console.error('Expected testimonies array but got:', typeof data, data);
        setConfessions([]);
        toast.error('Unexpected data format from server');
      }
    } catch (error) {
      console.error('Error fetching confessions:', error);
      toast.error('Failed to load confessions');
      setConfessions([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, ITEMS_PER_PAGE]);

  const scrollToGalleryTop = () => {
    const confessionsSection = document.getElementById('confessions');
    if (confessionsSection) {
      // Scroll with offset to account for header
      const headerOffset = 80;
      const elementPosition = confessionsSection.offsetTop;
      const offsetPosition = elementPosition - headerOffset;
      
      // First scroll, then focus after scroll completes
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Wait for scroll to complete before moving focus
      setTimeout(() => {
        // Add aria-live region announcement for screen readers
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.className = 'sr-only';
        announcement.textContent = `Page ${currentPage} loaded. ${totalCount} total confessions.`;
        document.body.appendChild(announcement);
        
        // Make section focusable and focus it
        confessionsSection.setAttribute('tabindex', '-1');
        confessionsSection.focus({ preventScroll: true }); // Prevent additional scrolling
        
        // Clean up
        setTimeout(() => {
          confessionsSection.removeAttribute('tabindex');
          document.body.removeChild(announcement);
        }, 1000);
      }, 500); // Wait for smooth scroll to complete
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      // Small delay to ensure content loads before scrolling
      setTimeout(scrollToGalleryTop, 100);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      // Small delay to ensure content loads before scrolling
      setTimeout(scrollToGalleryTop, 100);
    }
  };

  const handlePageClick = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      // Small delay to ensure content loads before scrolling  
      setTimeout(scrollToGalleryTop, 100);
    }
  };

  const checkTokenOwnership = async () => {
    if (!isConnected || !address) return;
    
    try {
      setCheckingTokens(true);
      debugLog('🔍 Checking token ownership for gallery:', address);

      // Get wallet's token balances from Base Mainnet
      const apiUrl = `https://base.blockscout.com/api/v2/addresses/${address}/token-balances`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      let walletTokens = [];
      if (response.ok) {
        const data = await response.json();
        walletTokens = data
          .filter(token => token.token && parseFloat(token.value) > 0)
          .map(token => token.token.address_hash.toLowerCase());
        
        debugLog('💰 Wallet tokens in gallery:', walletTokens);
      }

      // Check ownership for each confession
      const ownership = {};
      confessions.forEach(confession => {
        if (confession.nftContractAddress) {
          ownership[confession.id] = walletTokens.includes(confession.nftContractAddress.toLowerCase());
        } else {
          ownership[confession.id] = false;
        }
      });

      setTokenOwnership(ownership);
      debugLog('🔓 Token ownership map:', ownership);
      
    } catch (error) {
      console.error('Error checking token ownership:', error);
      setTokenOwnership({});
    } finally {
      setCheckingTokens(false);
    }
  };

  const handlePlayPause = (confessionId) => {
    // Find the video element in the DOM for this confession
    const videoElement = document.getElementById(`video-${confessionId}`);
    
    if (!videoElement) {
      console.error('Video element not found for confession:', confessionId);
      toast.error('Unable to play media. Please refresh the page.');
      return;
    }

    if (playingId === confessionId) {
      videoElement.pause();
      setPlayingId(null);
    } else {
      // Pause any currently playing media
      Object.values(mediaElements).forEach(media => {
        if (media.pause) media.pause();
      });
      
      // Pause all other video elements
      document.querySelectorAll('video').forEach(video => {
        if (video !== videoElement) video.pause();
      });
      
      videoElement.play().catch(error => {
        console.error('Play failed:', error);
        toast.error('Unable to play media. Please try again.');
        setPlayingId(null);
      });
      setPlayingId(confessionId);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleViewCoin = (confessionId) => {
    setSelectedConfessionId(confessionId);
    setIsCoinModalOpen(true);
  };

  const handleCloseCoinModal = () => {
    setIsCoinModalOpen(false);
    setSelectedConfessionId(null);
  };

  const handleTabKeyDown = (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const newTab = activeTab === 'all' ? 'unlocked' : 'all';
      setActiveTab(newTab);
      
      // Focus the newly selected tab
      setTimeout(() => {
        const tabToFocus = document.getElementById(
          newTab === 'all' ? 'all-testimonies-tab' : 'unlocked-testimonies-tab'
        );
        if (tabToFocus) {
          tabToFocus.focus();
        }
      }, 0);
    }
  };


  const getUnlockedConfessions = () => {
    return confessions.filter(confession => tokenOwnership[confession.id]);
  };

  const getCurrentConfessions = () => {
    if (activeTab === 'unlocked') {
      return getUnlockedConfessions();
    }
    return confessions;
  };

  const renderTabContent = () => {
    if (activeTab === 'unlocked') {
      if (!isConnected) {
        return (
          <motion.div
            className="empty-state card glass"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="empty-icon">🔐</div>
            <h3>Connect Your Wallet</h3>
            <p>Connect your wallet to see confessions you have unlocked by owning their tokens.</p>
            <div className="connect-button-wrapper">
              <ConnectButton chainStatus="none" />
            </div>
          </motion.div>
        );
      }

      const unlockedConfessions = getUnlockedConfessions();
      if (unlockedConfessions.length === 0) {
        return (
          <motion.div
            className="empty-state card glass"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="empty-icon">🔓</div>
            <h3>No Unlocked Confessions</h3>
            <p>You don't own any confession tokens yet. Purchase tokens from the gallery below to unlock full confessions.</p>
            <motion.button
              className="btn btn-primary"
              onClick={() => setActiveTab('all')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Browse All Confessions
            </motion.button>
          </motion.div>
        );
      }
    }

    const confessionsToShow = getCurrentConfessions();
    if (confessionsToShow.length === 0 && activeTab === 'all') {
      return (
        <motion.div
          className="empty-state card glass"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="empty-icon"></div>
          <h3>No Confessions Yet</h3>
          <p>Be the first to share your story and create meaningful change.</p>
          <motion.button
            className="btn btn-primary"
            onClick={() => document.getElementById('submit')?.scrollIntoView({ behavior: 'smooth' })}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Share Your Story
          </motion.button>
        </motion.div>
      );
    }

    return (
      <>
        <div className="testimonies-grid">
          {confessionsToShow.map((confession, index) => (
            <motion.div
              key={confession.id}
              className="testimony-card card"
              initial={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: (index % 20) * 0.05 }}
              whileHover={{ y: -5 }}
            >
              <div className="testimony-header">
                <div className="testimony-meta">
                  <span className="testimony-id">
                    #{confession.testimonyNumber || 'N/A'}
                  </span>
                  <span className="testimony-date">
                    {formatDate(confession.createdAt)}
                  </span>
                  <div className="audio-info">
                    <Clock size={14} />
                    <span>{formatDuration(confession.audioDuration)}</span>
                  </div>
                </div>
                
                <div className="lock-status">
                  {checkingTokens ? (
                    <div className="checking-tokens">
                      <div className="spinner small"></div>
                      <span>Checking...</span>
                    </div>
                  ) : isConnected ? (
                    tokenOwnership[confession.id] ? (
                      <div className="unlocked-status">
                        <Unlock size={16} />
                        <span>Full Access</span>
                      </div>
                    ) : !confession.hasUnlockableContent ? (
                      <div className="free-status">
                        <Unlock size={16} />
                        <span>Full Access</span>
                      </div>
                    ) : (
                      <div className="locked-status">
                        <Lock size={16} />
                        <span>Preview Only</span>
                      </div>
                    )
                  ) : !confession.hasUnlockableContent ? (
                    <div className="free-status">
                      <Unlock size={16} />
                      <span>Full Access</span>
                    </div>
                  ) : (
                    <div className="no-wallet-status">
                      <Lock size={16} />
                      <span>Connect wallet to unlock</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="testimony-content">
                <p className="testimony-text">{confession.textContent}</p>
              </div>

              <div className="media-player">
                <div className="video-container">
                  <video
                    id={`video-${confession.id}`}
                    src={
                      tokenOwnership[confession.id] && confession.fullAudioUrl 
                        ? confession.fullAudioUrl 
                        : confession.previewAudioUrl
                    }
                    poster={confession.imageUrl}
                    preload="metadata"
                    muted={false}
                    playsInline
                    className="waveform-video"
                    onEnded={(e) => {
                      // Reset video to show poster again and stop playing
                      e.target.load();
                      setPlayingId(null);
                    }}
                    onError={(e) => {
                      console.error('Video error:', e);
                      toast.error('Unable to load media');
                    }}
                    key={`${confession.id}-${tokenOwnership[confession.id] ? 'full' : 'preview'}`}
                  />
                  <button
                    className={`play-overlay ${playingId === confession.id ? 'playing' : ''}`}
                    onClick={() => handlePlayPause(confession.id)}
                    aria-label={
                      playingId === confession.id 
                        ? `Pause confession: ${confession.textContent.substring(0, 50)}...`
                        : `Play confession: ${confession.textContent.substring(0, 50)}...`
                    }
                    aria-pressed={playingId === confession.id}
                  >
                    {playingId === confession.id ? 
                      <Pause size={24} aria-hidden="true" /> : 
                      <Play size={24} aria-hidden="true" />
                    }
                  </button>
                </div>
                <div className="media-controls">
                  <span className="media-label">
                    {!confession.hasUnlockableContent 
                      ? 'Full Audio' 
                      : (tokenOwnership[confession.id] ? 'Full Audio' : 'Preview')
                    }
                  </span>
                </div>
              </div>

              {confession.ngos && confession.ngos.length > 0 && (
                <div className="supported-ngos">
                  <h4>Supporting:</h4>
                  <div className="ngo-tags">
                    {confession.ngos.map(ngo => (
                      <span key={ngo.id} className="ngo-tag">
                        <Heart size={12} />
                        {ngo.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="testimony-actions">
                <motion.button
                  className="btn btn-primary btn-small"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const platformWallet = process.env.REACT_APP_PLATFORM_ADDRESS;
                    const zoraUrl = `https://zora.co/coin/base:${confession.nftContractAddress}?referrer=${platformWallet}`;
                    
                    console.log('🔍 Debug info:', {
                      isMiniApp,
                      hasOpenUrl: !!openUrl,
                      zoraUrl,
                      userAgent: navigator.userAgent.substring(0, 100)
                    });
                    
                    if (isMiniApp && openUrl) {
                      openUrl(zoraUrl);
                    } else {
                      window.open(zoraUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  aria-label={`Check confession coin on Zora: ${confession.textContent.substring(0, 50)}...`}
                >
                  🪙 Check on Zora
                </motion.button>
                
                <motion.button
                  className="btn btn-secondary btn-small"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleViewCoin(confession.id)}
                  aria-label={`Open coin details for confession: ${confession.textContent.substring(0, 50)}...`}
                >
                  <ExternalLink size={16} aria-hidden="true" />
                  Open
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && activeTab === 'all' && (
          <div className="pagination-container">
            <motion.button
              className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              whileHover={currentPage > 1 ? { scale: 1.05 } : {}}
              whileTap={currentPage > 1 ? { scale: 0.95 } : {}}
            >
              <ChevronLeft size={20} />
              Previous
            </motion.button>

            <div className="pagination-numbers">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 7) {
                  pageNumber = i + 1;
                } else if (currentPage <= 4) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNumber = totalPages - 6 + i;
                } else {
                  pageNumber = currentPage - 3 + i;
                }

                return (
                  <motion.button
                    key={pageNumber}
                    className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => handlePageClick(pageNumber)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {pageNumber}
                  </motion.button>
                );
              })}
            </div>

            <motion.button
              className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              whileHover={currentPage < totalPages ? { scale: 1.05 } : {}}
              whileTap={currentPage < totalPages ? { scale: 0.95 } : {}}
            >
              Next
              <ChevronRight size={20} />
            </motion.button>

            <div className="pagination-info">
              <span>
                Page {currentPage} of {totalPages} ({totalCount} total confessions)
              </span>
            </div>
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <section id="confessions" className="gallery-section section">
        <div className="container">
          <div className="loading-state">
            <div className="spinner large"></div>
            <p>Loading confessions...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="confessions" className="gallery-section section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 1, y: 0 }}
          transition={ { duration: 0.6 }}
        >
          <h2>Confession Gallery</h2>
          <p>Explore stories from our community and support meaningful causes</p>
        </motion.div>

        <motion.div
          className="gallery-tabs"
          initial={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          role="tablist"
          aria-label="Gallery view options"
        >
          <motion.button
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
            onKeyDown={handleTabKeyDown}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            role="tab"
            aria-selected={activeTab === 'all'}
            aria-controls="all-testimonies-panel"
            id="all-testimonies-tab"
            tabIndex={activeTab === 'all' ? 0 : -1}
          >
            All Confessions
            <span className="tab-count" aria-label={`${totalCount > 0 ? totalCount : confessions.length} confessions`}>
              {totalCount > 0 ? totalCount : confessions.length}
            </span>
          </motion.button>
          <motion.button
            className={`tab-button ${activeTab === 'unlocked' ? 'active' : ''}`}
            onClick={() => setActiveTab('unlocked')}
            onKeyDown={handleTabKeyDown}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            role="tab"
            aria-selected={activeTab === 'unlocked'}
            aria-controls="unlocked-testimonies-panel"
            id="unlocked-testimonies-tab"
            tabIndex={activeTab === 'unlocked' ? 0 : -1}
          >
            <Unlock size={16} aria-hidden="true" />
            Unlocked Confessions
            <span className="tab-count" aria-label={`${getUnlockedConfessions().length} unlocked confessions`}>
              {getUnlockedConfessions().length}
            </span>
          </motion.button>
        </motion.div>

        <div
          role="tabpanel"
          id={activeTab === 'all' ? 'all-testimonies-panel' : 'unlocked-testimonies-panel'}
          aria-labelledby={activeTab === 'all' ? 'all-testimonies-tab' : 'unlocked-testimonies-tab'}
          tabIndex={0}
        >
          {renderTabContent()}
        </div>
        
        <CoinModal
          isOpen={isCoinModalOpen}
          onClose={handleCloseCoinModal}
          confessionId={selectedConfessionId}
        />
      </div>
    </section>
  );
});

export default ConfessionGallery;